import {
    DialogActionButton,
    NewLOCModalV3,
    TextField,
} from '@finpay-development/shared-components';
import {
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    MenuItem,
    Typography,
} from '@mui/material';
import {FormikProps, useFormik} from 'formik';
import React, {useEffect, useMemo, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';

import {clearStatus} from '../../../admin-configuration/state/admin-configuration-slice';
import {ImplementationFacility} from '../../../implementation-specialist/components/implementation-clients/details/models/implementation-facility';
import {
    showErrorStatus,
    showStatus,
} from '../../../security/state/user-slice';
import {RootState} from '../../../shared/state/root-reducer';
import {
    Estimate as NewEstimate,
    QuoteMethod,
    FacilityLevelOfCareWithCrossoverDays,
    ThirdPartyPayerStatus,
    PriorCareProvider,
    PriorCare,
    FacilityLevelOfCare,
    SummarySelections,
    Vob,
    VobClassification,
    PayorPlan,
} from '@finpay/estimation-types';
import {VobPostBody} from 'src/admissions-advisor/models/vob';
import {saveSelectedEstimate} from 'src/shared/state/estimator-thunk';
import {Utils} from 'src/shared/utils';
import {saveVob} from 'src/admissions-advisor/state/vob-thunk';
import {AppDispatch} from 'src/shared/state/store';
import {admissionsAdvisorService} from 'src/admissions-advisor/services/admissions-advisor-services';
import {getFormikErrMsg} from '../../../admissions-advisor/components/pfr-estimator/estimator-loc-modal/estimator-loc-modal';
import {loadAndSetRateAsPayorPlan, findMatchedPayorPlan} from '../../utils/payor-plan-utils';

interface EstPriorCareModalProps {
    open: boolean;
    isEdit: boolean;
    facilities: ImplementationFacility[][];
    facilityId: number;
    handlePriorCareModalCancel: () => void;
    handlePriorCareModalSubmit: (isEdit: boolean) => void;
    vob: Vob;
    initialListOfLoc: FacilityLevelOfCareWithCrossoverDays[];
    priorCare?: PriorCare;
}

interface NameValue {
    name: string;
    value: number;
}
const PriorCareModal = (props: EstPriorCareModalProps) => {
    const {
        open,
        isEdit,
        facilities,
        facilityId,
        handlePriorCareModalCancel,
        handlePriorCareModalSubmit,
        vob,
        initialListOfLoc,
        priorCare,
    } = props;

    const estimate = useSelector((state: RootState) => {
        return state.estimatorContext.selectedEstimate;
    });
    const vobClassifications = useSelector(
        (state: RootState) =>
            state.vobContext.vobClassifications
    );
    const vobPatientState = useSelector(
        (state: RootState) => state.admissionsAdvisorContext.vobPatientContext
    );

    const rateAsPayorPlan = useSelector(
        (state: RootState) => state.estimatorContext.rateAsPayorPlan
    ) as PayorPlan | null;

    const payors = useSelector(
        (state: RootState) =>
            state.adminContext.adminConfigurationContext?.payers
    );

    const saveStatus = useSelector(
        (state: RootState) =>
            state.adminContext.adminConfigurationContext.modalSaveStatus
    );
    const reduxRateAsPayorMap = useSelector((state: RootState) => state.estimatorContext.rateAsPayorMap) || {};
    const reduxMasterPayors = useSelector((state: RootState) => state.adminContext.adminConfigurationContext?.payersMap) || new Map();

    const errorMessage = useSelector(
        (state: RootState) =>
            state.adminContext.adminConfigurationContext.errorMessage
    );

    const priorCareProviders: NameValue[] = [
        {name: 'Client', value: PriorCareProvider.CLIENT},
        {name: 'Third Party', value: PriorCareProvider.THIRDPARTY},
    ];

    const thirdPartyProviderOptions: NameValue[] = [
        {name: 'Average In-Network', value: ThirdPartyPayerStatus.INNETW},
        {name: 'Average Out-of-Network', value: ThirdPartyPayerStatus.OONETW},
    ];

    const [listOfLoc, setListOfLoc] =
        useState<FacilityLevelOfCareWithCrossoverDays[]>(initialListOfLoc);

    useEffect(()=>{
        setListOfLoc(initialListOfLoc)
    }, [initialListOfLoc])
    
    const [modalFormik, setModalFormik] = useState<FormikProps<any> | null>(
        null
    );

    const dispatch = useDispatch<AppDispatch>();

    const priorCareModalFormik = useFormik({
        initialValues: {
            priorCareProvider:
                priorCare?.priorCareProvider || PriorCareProvider.CLIENT,
            thirdPartyPayerStatus:
                priorCare?.thirdPartyPayerStatus ||
                ThirdPartyPayerStatus.INNETW,
            facility: priorCare?.facilityId || facilityId,
        },
        onSubmit: () => {},
    });

    const handleNewSave = async () => {
        const formikErrorMsg = getFormikErrMsg(modalFormik?.errors)

        if(formikErrorMsg){
            dispatch(showErrorStatus(formikErrorMsg))
            return
        }

        if(!modalFormik?.dirty){
            dispatch(showStatus("No changes detected. Nothing to save."))
            return
        }

        const estimateCopy = Utils.deepClone(estimate);
        const {vobClassificationUpdates} = modalFormik?.values;

        const vobUpdateKeys = Object.keys(vobClassificationUpdates);
        const vobNeedsUpdate = vobUpdateKeys.length > 0;
        const inNetwork: boolean = rateAsPayorPlan?.payor?.inNetwork!;

        if (vobNeedsUpdate) {
            const vobCopy: Vob = Utils.deepClone(vob);

            const inNetwVobClassifications =
                vobCopy.innClassifications || [];

            const ooNetwVobClassifications =
                vobCopy.oonClassifications || [];

            vobUpdateKeys.forEach(key => {
                const {
                    toggleButtonSelection,
                    coPay,
                    coInsurance,
                    maxDays,
                    vobClassification,
                } = vobClassificationUpdates[key];
                let coPaySelection;

                switch (toggleButtonSelection) {
                    case 0:
                        coPaySelection = 'copayonly';
                        break;
                    case 1:
                        coPaySelection = 'n';
                        break;
                    case 2:
                        coPaySelection = 'y';
                        break;
                    default:
                        coPaySelection = 'copayonly';
                        break;
                }

                //not sure
                // if (inNetwork) {
                //     inNetwVobClassifications.push({
                //         vobClassificationId: vobClassification.vobClassificationId,
                //         vobClassificationName: key,
                //         vobClassificationDesc: key,
                //         isInpatient: vobClassification.isInpatient,
                //         coPaySelection,
                //         coInsurance,
                //         coPay,
                //         maxDays,
                //     });
                // } else {
                //     ooNetwVobClassifications.push({
                //         id: Math.random(),
                //         selectedClassification: {
                //             vobClassificationId:
                //                 vobClassification.vobClassificationId,
                //             vobClassificationName: key,
                //             vobClassificationDesc: key,
                //             isInpatient: vobClassification.isInpatient,
                //         },
                //         coPaySelection,
                //         coInsurance,
                //         coPay,
                //         maxDays,
                //     });
                // }
            });

            // const vobPostBody: VobPostBody = {
            //     advisorPatientId: vobPatientState.patient.advisorPatientId,
            //     fpClientId: vobCopy.client?.clientId,
            //     fpClientFacilityId: vobCopy.facility?.facilityId,
            //     vobBody: {
            //         ...vobCopy,
            //         innClassifications: inNetwVobClassifications as VobClassification[],
            //         oonClassifications: ooNetwVobClassifications as VobClassification[],
            //     },
            // };
            // try {
            //     await dispatch(saveVob(vobPostBody));
            // } catch (e) {
            //     dispatch(showErrorStatus('Error updating VOB'));
            // }
        }
        const {
            estimateId,
            crossOverSummary,
            summary,
            priorCareSummary,
            createUserId,
            createDt,
            lastUpdateUserId,
            lastUpdateDt,
            ...rest
        } = estimateCopy;

        const putEstimateBody: NewEstimate = {
            ...rest,
            summarySelection: SummarySelections.PRIORCARE,
            priorCare: {
                priorCareProvider:
                    priorCareModalFormik.values.priorCareProvider,
                thirdPartyPayerStatus:
                    priorCareModalFormik?.values?.thirdPartyPayerStatus,
                facilityId: priorCareModalFormik.values.facility,
                loc: modalFormik?.values?.selectedLocs,
            },
            rateAsPayorPlan: rateAsPayorPlan,
        };
        try {
            await dispatch(
                saveSelectedEstimate({estimate: putEstimateBody, estimateId})
            );
            
            // Recalculate rateAsPayorPlan for the main estimate facility - should it prior care facility? 
            if (vob?.facility && payors) {
                await loadAndSetRateAsPayorPlan(
                    vob.client,
                    vob.facility,
                    dispatch,
                    reduxMasterPayors,
                    vob.payor,
                    vob.plan,
                    reduxRateAsPayorMap
                );
            }
            
            handleSaveCallback(true);
        } catch (e) {
            dispatch(showErrorStatus('Error updating Prior Care'));
        }
    };

    function handleSaveCallback(saveSuccessful: boolean) {
        if (saveSuccessful) {
            handlePriorCareModalSubmit(isEdit);
        } else {
            dispatch(showErrorStatus(errorMessage));
        }
        dispatch(clearStatus());
    }

    function handleCancelCallback() {
        handlePriorCareModalCancel();
    }

    const handleThirdPartyProviderChange = async (e: Event) => {
        const selection = Number((e.target as HTMLInputElement).value);
        const isClient = selection === PriorCareProvider.CLIENT;

        if (isClient) {
            const facilityId = priorCareModalFormik.values.facility;

            const response =
                await admissionsAdvisorService.getFacilityLevelsOfCare(
                    facilityId as number,
                    rateAsPayorPlan?.plan?.facilityPlanId!
                );

            setListOfLoc(response.hasErrors ? [] : response.entity);
        } else {
            const inn =
                priorCareModalFormik.values.thirdPartyPayerStatus ===
                ThirdPartyPayerStatus.INNETW
                    ? 'y'
                    : 'n';

            const response =
                await admissionsAdvisorService.getMasterFacilityLevelsOfCare(
                    facilityId,
                    inn
                );

            const newLoc = response.hasErrors
                ? []
                : response.entity?.filter((loc: FacilityLevelOfCare) => {
                      return loc.isCovered;
                  });

            setListOfLoc(newLoc);
        }

        modalFormik?.setValues({
            selectedLocs: [],
            vobClassificationUpdates: {}
        }, true)

        priorCareModalFormik.setFieldValue('priorCareProvider', selection);
    };

    const handleThirdPartyOptionsChange = async (e: Event) => {
        const selection = Number((e.target as HTMLInputElement).value);
        const inn = selection === ThirdPartyPayerStatus.INNETW ? 'y' : 'n';

        const response =
            await admissionsAdvisorService.getMasterFacilityLevelsOfCare(
                facilityId,
                inn
            );

        const newLoc = response.hasErrors
            ? []
            : response.entity?.filter((loc: FacilityLevelOfCare) => {
                  return loc.isCovered;
              });

        modalFormik?.setFieldValue('selectedLocs', [], true);
        setListOfLoc(newLoc);
    };

    const updateSelectedFacility = async (e: Event) => {
        const facilityId = +(e.target as HTMLInputElement).value;
        const responsePayers = await admissionsAdvisorService.getEstimatorFacilityLocPayers(
            -2,
            Number(facilityId)
        );
        if (!responsePayers.hasErrors && responsePayers.entity?.facilityPayors) {
            const matchedPlan = findMatchedPayorPlan(
                responsePayers.entity.facilityPayors,
                vob.payor,
                vob.plan,
                [], // TODO: Empty array for payorMap as we don't need it for direct matching
                new Map() // Empty map for masterPayors as we don't need it for direct matching
            );

            if (matchedPlan) {
                const facilityPlanId = matchedPlan.plan.facilityPlanId || 0;
                const response = await admissionsAdvisorService.getFacilityLevelsOfCare(
                    facilityId,
                    facilityPlanId
                );
                // TODO: need to return empty array instead of nothing
                setListOfLoc(response.hasErrors ? [] : response.entity);
                modalFormik?.setFieldValue('selectedLocs', [], true);
            } else {
                setListOfLoc([]);
                dispatch(showErrorStatus('No Levels of Care available for this Facility/Payer'));
            }
        }
    };

    const isValid = Boolean(modalFormik?.isValid && modalFormik?.dirty);

    const filteredListOfLoc = useMemo(() => listOfLoc.filter(loc => loc.locType === 0), [listOfLoc])

    return (
        <Dialog
            className="modal"
            open={open}
            fullWidth={true}
            maxWidth="lg"
            scroll="body"
        >
            <DialogTitle>
                {isEdit ? 'Edit' : 'Add'} Prior Care
            </DialogTitle>
            <DialogContent test-id="prior-care-modal-content">
                <>
                    <Grid
                        container
                        spacing={2}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                        }}
                    >
                        <Grid item direction="column" md={6} xs={6}>
                            <TextField
                                select
                                label="Prior Care Provider"
                                name="priorCareProvider"
                                value={
                                    priorCareModalFormik.values
                                        .priorCareProvider
                                }
                                onChange={(e: Event) => {
                                    priorCareModalFormik.handleChange(e);
                                    handleThirdPartyProviderChange(e);
                                }}
                                onBlur={priorCareModalFormik.handleBlur}
                                error={
                                    priorCareModalFormik.touched
                                        .priorCareProvider &&
                                    priorCareModalFormik.errors
                                        .priorCareProvider
                                }
                            >
                                {priorCareProviders.map(
                                    (provider: NameValue) => (
                                        <MenuItem
                                            key={provider.value}
                                            value={provider.value}
                                        >
                                            {provider.name}
                                        </MenuItem>
                                    )
                                )}
                            </TextField>
                        </Grid>
                        <Grid item direction="column" md={6} xs={6}>
                            {priorCareModalFormik.values.priorCareProvider ===
                            PriorCareProvider.THIRDPARTY ? (
                                <TextField
                                    select
                                    label="Third Party Payer Status"
                                    name="thirdPartyPayerStatus"
                                    value={
                                        priorCareModalFormik.values
                                            .thirdPartyPayerStatus
                                    }
                                    onChange={(e: Event) => {
                                        priorCareModalFormik.handleChange(e);
                                        handleThirdPartyOptionsChange(e);
                                    }}
                                    onBlur={priorCareModalFormik.handleBlur}
                                    error={
                                        priorCareModalFormik.touched
                                            .thirdPartyPayerStatus &&
                                        priorCareModalFormik.errors
                                            .thirdPartyPayerStatus
                                    }
                                >
                                    {thirdPartyProviderOptions.map(
                                        (option: NameValue) => (
                                            <MenuItem
                                                key={option.value}
                                                value={option.value}
                                            >
                                                {option.name}
                                            </MenuItem>
                                        )
                                    )}
                                </TextField>
                            ) : (
                                <TextField
                                    select
                                    label="Facility"
                                    name="facility"
                                    value={priorCareModalFormik.values.facility}
                                    onChange={(e: Event) => {
                                        priorCareModalFormik.handleChange(e);
                                        updateSelectedFacility(e);
                                    }}
                                    onBlur={priorCareModalFormik.handleBlur}
                                    error={
                                        priorCareModalFormik.touched.facility &&
                                        priorCareModalFormik.errors.facility
                                    }
                                >
                                    {facilities
                                        .flat()
                                        .map(
                                            (
                                                facility: ImplementationFacility
                                            ) => (
                                                <MenuItem
                                                    key={facility.facilityId}
                                                    value={facility.facilityId}
                                                >
                                                    {facility.facilityName}
                                                </MenuItem>
                                            )
                                        )}
                                </TextField>
                            )}
                        </Grid>
                    </Grid>
                    {rateAsPayorPlan ? (
                        <NewLOCModalV3
                            // always manual for the allow user to choose days/sessions
                            quoteMethod={QuoteMethod.MANUAL}
                            initialListOfSelectedLoc={
                                estimate?.priorCare
                                    ?.loc as FacilityLevelOfCareWithCrossoverDays[]
                            }
                            listOfLoc={filteredListOfLoc}
                            // set the date to the epoch, 1970-01-01
                            admissionDate={new Date(0)}
                            policyEndDate={new Date(vob.policyEndDate)}
                            vob={vob}
                            rateAsPayorPlan={rateAsPayorPlan}
                            vobClassifications={[]}
                            getFormikValues={formik => {
                                setModalFormik(formik as FormikProps<any>);
                            }}
                        />
                    ) : (
                        <Typography color="error" sx={{marginY: '1rem'}}>
                            Rate as payer plan information is missing. Please check VOB before continuing.
                        </Typography>
                    )}
                </>
            </DialogContent>
            <DialogActions test-id="prior-care-modal-actions">
                <DialogActionButton
                    test-id="prior-care-modal-save-button"
                    isEnabled={true}
                    savebuttonText={isEdit ? 'Update' : 'Save'}
                    saveStatus={saveStatus}
                    spinnerLeftPosition={5}
                    executeSave={handleNewSave}
                    handleCallbackSave={handleSaveCallback}
                    handleCallbackCancel={handleCancelCallback}
                />
            </DialogActions>
        </Dialog>
    );
};

export default PriorCareModal;
